import { Divider, Grid, Typography } from '@mui/material';
import { cloneDeep, first, range, toString } from 'lodash';
import { Controller } from 'react-hook-form';
import { NumberInputWidget } from 'src/components/widget';
import {
  QuillTheme,
  RichTextEditor,
  RichTextToolbarOptions,
} from 'src/components/widget/richTextWidget';
import { extractStylesFromHtml } from 'src/components/widget/richTextWidget/RichTextWidget.helpers';
import { FONTS_OPTIONS } from 'src/constants';
import {
  useGetProjectConfig,
  useRteDefaultStyleValues,
} from 'src/hooks/api/projectConfig';
import { lang } from 'src/utils';

import { PositionBox } from '../../PositionBox';
import { MessagingControllerProps } from './MessagingController.interface';

const RICH_TEXT_EDITOR_OPTIONS = {
  placeholder: 'Enter text...',
  richTextTheme: QuillTheme.TOOLBAR,
};

export const MessagingController = ({
  control,
  customer,
  onChange: handleOnChange,
  errors,
  theme,
}: MessagingControllerProps) => {
  const { data: projectConfig } = useGetProjectConfig();

  const projectFonts = projectConfig?.fonts?.map((item) => item.name) ?? [];
  const mappedFonts = projectFonts.length > 0 ? projectFonts : FONTS_OPTIONS;

  const rteDefaultStyleValues = useRteDefaultStyleValues(theme, projectConfig);
  const hiddenToolbarItems: RichTextToolbarOptions[] = [
    RichTextToolbarOptions.STRIKE,
    RichTextToolbarOptions.SIZE,
  ];

  return (
    <Grid container gap={1} padding={2}>
      <Controller
        key={`${customer}.label`}
        name={`${customer}.label`}
        control={control}
        render={({ field }) => {
          const firstLabel = first(field.value);
          const firstError = errors?.label?.[0];

          return (
            <>
              <Grid container gap={1} direction="column">
                {errors?.label && (
                  <Typography color="error" variant="caption">
                    {errors?.label?.message}
                  </Typography>
                )}
                <Grid item>
                  <NumberInputWidget
                    {...field}
                    label={lang.fontSize}
                    value={firstLabel?.fontSize}
                    error={firstError?.fontSize?.message}
                    hasError={!!firstError?.fontSize}
                    onChange={(value) => {
                      const label = cloneDeep(field.value || []);
                      label[0] = {
                        ...firstLabel,
                        fontSize: toString(value),
                        value: firstLabel?.value || '',
                      };
                      handleOnChange(
                        `${customer}.label`,
                        label,
                        field.onChange,
                      );
                    }}
                    options={{ suffix: 'px' }}
                  />
                </Grid>
                <Grid item>
                  <RichTextEditor
                    placeholder={RICH_TEXT_EDITOR_OPTIONS.placeholder}
                    value={firstLabel?.value}
                    onChange={(value) => {
                      const label = cloneDeep(field.value || []);
                      const htmlValue = toString(value);
                      const extractedStyles = extractStylesFromHtml(htmlValue);

                      label[0] = {
                        ...firstLabel,
                        value: htmlValue,
                        font: extractedStyles.fontFamily || firstLabel?.font,
                      };
                      handleOnChange(
                        `${customer}.label`,
                        label,
                        field.onChange,
                      );
                    }}
                    disabled={false}
                    theme={RICH_TEXT_EDITOR_OPTIONS.richTextTheme}
                    fontFamily={mappedFonts}
                    fontSize={range(8, 200).map((size) => `${size}px`)}
                    letterSpacing={['0px', '1px', '2px']}
                    hide={hiddenToolbarItems}
                    defaultStyleValues={{
                      fontFamily: rteDefaultStyleValues.fontFamily,
                    }}
                  />
                  {firstError?.value && (
                    <Typography color="error" variant="caption">
                      {firstError?.value?.message}
                    </Typography>
                  )}
                </Grid>
                <PositionBox
                  x={firstLabel?.left || 0}
                  y={firstLabel?.top || 0}
                  onChange={(x, y) => {
                    const label = cloneDeep(field.value || []);
                    label[0] = {
                      ...firstLabel,
                      left: x,
                      top: y,
                      value: firstLabel?.value || '',
                    };
                    handleOnChange(`${customer}.label`, label, field.onChange);
                  }}
                />
                <Divider />
              </Grid>
            </>
          );
        }}
      />
    </Grid>
  );
};
